/* eslint-disable import/no-duplicates */
import {
  MdOutlineMail,
  MdOutlinePictureAsPdf,
} from "react-icons/md";
import { Link } from "react-router-dom";
import { FiCalendar, FiEdit2, FiCheck, FiX, FiSend } from "react-icons/fi";
import { BsTrash } from "react-icons/bs";
import { AiOutlineCheck } from "react-icons/ai";
import { useState } from "react";
import moment from "moment";
import { ModalUpdateStatusTechVisitReport } from "./ModalUpdateStatusTechVisitReport";
import { ModalSendMail } from "../pages/TechVisitReport/components/ModalSendMail";

interface Service {
  name: string;
}

interface ItemPaginationReportProps {
  id: string;
  title: string;
  begin: string;
  pending: boolean;
  sent: boolean;
  status: string;
  services: Service[];
  costumer_id: string;
  code: string;
  type?: string;
  canEdit?: boolean;
  canDelete?: boolean;
  setPagination(): void;
}

export function ItemPaginationReport({
  id,
  title,
  begin,
  pending,
  sent,
  status,
  services,
  costumer_id,
  code,
  type,
  canEdit = true,
  canDelete = true,
  setPagination,
}: ItemPaginationReportProps) {
  const date = moment(begin).format("DD/MM/YYYY");
  const time = moment(begin).format("HH:mm");

  const [modal, showModal] = useState(false);
  const [modalSendMail, showModalSendMail] = useState(false);

  // Determinar a rota de edição baseado no tipo de relatório
  const editPath = type === "delivery" ? `/delivery-reports/edit/${id}` : `edit/${id}`;

  return (
    <>
      <ModalUpdateStatusTechVisitReport
        showModal={modal}
        onHide={() => showModal(false)}
        statusUpdated={() => {
          setPagination();
        }}
        id={id}
        status={status === "A" ? "I" : "A"}
      />
      <ModalSendMail
        showModalSendMail={modalSendMail}
        onHide={() => showModalSendMail(false)}
        costumer_id={costumer_id}
        report_id={id}
      />

      <div className="list-item-enter group relative flex flex-col sm:flex-row sm:items-center justify-between w-full rounded-xl bg-white p-3 sm:p-4 shadow-sm border border-gray-100 hover:shadow-md hover:border-gray-200 transition-all duration-200">
        {/* Indicador lateral de status */}
        <div
          className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 rounded-r-full transition-all duration-200 ${
            status === "A" ? "bg-green-500" : "bg-gray-300"
          }`}
        />

        {/* Conteúdo principal */}
        <div className="flex flex-col gap-2 sm:gap-3 pl-3 flex-1 min-w-0">
          {/* Header: Código, Título e Toggle Mobile */}
          <div className="flex items-start gap-2 sm:gap-3">
            <span className="shrink-0 px-2 py-0.5 sm:py-1 bg-[#0085BD]/10 text-[#0085BD] text-xs font-bold rounded-lg">
              #{code}
            </span>
            <h1 className="flex-1 min-w-0 text-[#0085BD] font-semibold text-sm sm:text-base group-hover:text-[#006a96] transition-colors leading-tight line-clamp-2 sm:line-clamp-none">
              {title}
            </h1>
            {/* Toggle pequeno no canto - Mobile */}
            {canDelete && (
              <div className="flex sm:hidden shrink-0">
                <button
                  type="button"
                  onClick={() => showModal(true)}
                  className={`flex items-center justify-center w-8 h-8 rounded-full shadow-sm active:scale-95 transition-all duration-200 ${
                    status === "A"
                      ? "bg-green-100 text-green-600 hover:bg-green-200"
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                  }`}
                  title={status === "A" ? "Desativar" : "Ativar"}
                >
                  {status === "A" ? (
                    <AiOutlineCheck size={14} />
                  ) : (
                    <BsTrash size={12} />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Info Row: Data e Status Badges - Mobile optimizado */}
          <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3">
            {/* Data e Hora */}
            <div className="flex items-center gap-1.5 text-gray-500">
              <FiCalendar size={13} className="shrink-0" />
              <span className="text-xs sm:text-sm">{date}</span>
              <span className="text-gray-300">•</span>
              <span className="text-xs sm:text-sm">{time}</span>
            </div>

            {/* Status Badges - Com texto visível no mobile */}
            <div className="flex items-center gap-1.5 sm:gap-2">
              {/* Badge Enviado/Não Enviado */}
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-semibold rounded-full ${
                  sent
                    ? "bg-green-100 text-green-700 sm:bg-gradient-to-r sm:from-green-500 sm:to-emerald-500 sm:text-white"
                    : "bg-red-100 text-red-700 sm:bg-gradient-to-r sm:from-red-500 sm:to-rose-500 sm:text-white"
                }`}
              >
                {sent ? <FiCheck size={10} /> : <FiX size={10} />}
                <span>{sent ? "Enviado" : "Não enviado"}</span>
              </span>

              {/* Badge Pendente/Concluído */}
              <span
                className={`inline-flex items-center gap-1 px-2 py-0.5 sm:px-3 sm:py-1.5 text-[10px] sm:text-xs font-semibold rounded-full ${
                  pending
                    ? "bg-amber-100 text-amber-700 sm:bg-gradient-to-r sm:from-amber-500 sm:to-orange-500 sm:text-white"
                    : "bg-emerald-100 text-emerald-700 sm:bg-gradient-to-r sm:from-emerald-500 sm:to-teal-500 sm:text-white"
                }`}
              >
                {pending ? <FiX size={10} /> : <FiCheck size={10} />}
                <span>{pending ? "Pendente" : "Concluído"}</span>
              </span>
            </div>
          </div>

          {/* Serviços */}
          <div className="flex flex-wrap gap-1 sm:gap-1.5">
            {services.map(({ name }) => {
              return (
                <span
                  key={name}
                  className="inline-flex items-center gap-0.5 sm:gap-1 px-2 py-0.5 sm:px-2.5 sm:py-1 text-[10px] sm:text-xs font-semibold bg-gradient-to-r from-[#0085BD] to-[#006a96] text-white rounded-full"
                >
                  <span className="w-1 h-1 sm:w-1.5 sm:h-1.5 bg-white/40 rounded-full" />
                  {name}
                </span>
              );
            })}
          </div>

          {/* Botões de Ação - Mobile: Linha separada com botões maiores */}
          <div className="flex sm:hidden items-center gap-2 pt-2 border-t border-gray-100">
            {canEdit && (
              <Link
                to={editPath}
                className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-gradient-to-r from-[#0085BD] to-[#006a96] text-white text-sm font-medium shadow-md active:scale-[0.98] transition-all duration-200"
              >
                <FiEdit2 size={16} />
                <span>Editar</span>
              </Link>
            )}
            <Link
              to={{ pathname: `pdf/${id}` }}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white text-sm font-medium shadow-md active:scale-[0.98] transition-all duration-200"
            >
              <MdOutlinePictureAsPdf size={18} />
              <span>PDF</span>
            </Link>
            <button
              type="button"
              onClick={() => showModalSendMail(true)}
              className="flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white text-sm font-medium shadow-md active:scale-[0.98] transition-all duration-200"
            >
              <FiSend size={16} />
              <span>Enviar</span>
            </button>
          </div>

          {/* Botões de Ação - Desktop */}
          <div className="hidden sm:flex items-center gap-1.5">
            {canEdit && (
              <Link
                to={editPath}
                className="group/edit relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-[#0085BD] to-[#006a96] hover:from-[#006a96] hover:to-[#005a80] text-white text-xs font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
                title="Editar relatório"
              >
                <FiEdit2
                  size={12}
                  className="transition-transform duration-200 group-hover/edit:rotate-12"
                />
                <span>Editar</span>
                <div className="absolute inset-0 rounded-lg bg-white/0 group-hover/edit:bg-white/10 transition-colors duration-200" />
              </Link>
            )}
            <Link
              to={{ pathname: `pdf/${id}` }}
              target="_blank"
              rel="noopener noreferrer"
              className="group/pdf relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-xs font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              title="Ver PDF"
            >
              <MdOutlinePictureAsPdf
                size={14}
                className="transition-transform duration-200 group-hover/pdf:scale-110"
              />
              <span>PDF</span>
              <div className="absolute inset-0 rounded-lg bg-white/0 group-hover/pdf:bg-white/10 transition-colors duration-200" />
            </Link>
            <button
              type="button"
              onClick={() => showModalSendMail(true)}
              className="group/mail relative flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-xs font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              title="Enviar por e-mail"
            >
              <FiSend
                size={12}
                className="transition-transform duration-200 group-hover/mail:translate-x-0.5 group-hover/mail:-translate-y-0.5"
              />
              <span>Enviar</span>
              <div className="absolute inset-0 rounded-lg bg-white/0 group-hover/mail:bg-white/10 transition-colors duration-200" />
            </button>
          </div>
        </div>

        {/* Botão de Ativar/Desativar - Desktop */}
        {canDelete && (
          <div className="hidden sm:flex items-center ml-4">
            {status && status === "A" ? (
            <button
              type="button"
              onClick={() => showModal(true)}
              className="group/status relative flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white text-sm font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              title="Desativar relatório"
            >
              <AiOutlineCheck
                size={16}
                className="transition-transform duration-200 group-hover/status:scale-110"
              />
              <span>Ativo</span>
              <div className="absolute inset-0 rounded-xl bg-white/0 group-hover/status:bg-white/10 transition-colors duration-200" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => showModal(true)}
              className="group/status relative flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-gray-400 to-gray-500 hover:from-red-500 hover:to-red-600 text-white text-sm font-medium shadow-md hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200"
              title="Ativar relatório"
            >
              <BsTrash
                size={16}
                className="transition-transform duration-200 group-hover/status:scale-110"
              />
              <span>Inativo</span>
              <div className="absolute inset-0 rounded-xl bg-white/0 group-hover/status:bg-white/10 transition-colors duration-200" />
            </button>
          )}
          </div>
        )}
      </div>
    </>
  );
}
